import { FirestoreService } from '../../../../sdk/firestoreService';
import { StorageService } from '../../../../sdk/storageService';

// ============================================
// Action Creators - Dashboard Initialization
// ============================================

export const initializeDashboardStart = () => ({
  type: 'INITIALIZE_DASHBOARD_START'
});

export const initializeDashboardSuccess = (dashboard) => ({
  type: 'INITIALIZE_DASHBOARD_SUCCESS',
  payload: { dashboard }
});

export const initializeDashboardFailure = (error) => ({
  type: 'INITIALIZE_DASHBOARD_FAILURE',
  payload: error
});

// ============================================
// Action Creators - Dashboard Local Updates
// ============================================

export const updateDashboardLocal = (widgets) => ({
  type: 'UPDATE_DASHBOARD_LOCAL',
  payload: { widgets }
});

export const updateDashboardSettings = (settings) => ({
  type: 'UPDATE_DASHBOARD_SETTINGS',
  payload: { settings }
});

// ============================================
// Action Creators - Organization
// ============================================

export const loadOrganizationSuccess = (organization) => ({
  type: 'LOAD_ORGANIZATION_SUCCESS',
  payload: organization
});

// ============================================
// Action Creators - Image Upload
// ============================================

export const uploadImageStart = () => ({
  type: 'UPLOAD_IMAGE_START'
});

export const uploadImageSuccess = (imageUrl) => ({
  type: 'UPLOAD_IMAGE_SUCCESS',
  payload: imageUrl
});

export const uploadImageFailure = (error) => ({
  type: 'UPLOAD_IMAGE_FAILURE',
  payload: error
});

// ============================================
// Action Creators - Version Management
// ============================================

export const publishVersionStart = () => ({
  type: 'PUBLISH_VERSION_START'
});

export const publishVersionSuccess = (version) => ({
  type: 'PUBLISH_VERSION_SUCCESS',
  payload: { version }
});

export const publishVersionFailure = (error) => ({
  type: 'PUBLISH_VERSION_FAILURE',
  payload: error
});

export const loadVersionSuccess = (version) => ({
  type: 'LOAD_VERSION_SUCCESS',
  payload: { version }
});

// ============================================
// Thunk Actions - Dashboard Initialization
// ============================================

/**
 * initializeDashboard - טעינת דשבורד לפי ארגון
 * משמש כשנכנסים לאפליקציה עם משתמש מחובר
 */
export const initializeDashboard = (user) => async (dispatch) => {
  dispatch(initializeDashboardStart());

  try {
    // Load organization data
    console.log('📥 Loading organization:', user.organizationId);
    const organization = await FirestoreService.getOrganization(user.organizationId);
    console.log('✅ Organization loaded:', organization);

    if (!organization) {
      throw new Error('Organization not found');
    }
    dispatch(loadOrganizationSuccess(organization));

    // Load dashboard by organizationId
    console.log('📥 Loading dashboard for organization:', user.organizationId);
    let dashboard = await FirestoreService.getDashboardByOrganization(user.organizationId);

    if (!dashboard) {
      console.log('⚠️ No dashboard found');
      // אם אין דשבורד ואין משתמש מחובר (צפייה ציבורית), זרוק שגיאה
      if (!user.uid) {
        throw new Error('Dashboard not found for this organization');
      }
      // רק אם יש משתמש מחובר, צור דשבורד חדש
      console.log('⚠️ Creating new dashboard (authenticated user)');
      dashboard = await FirestoreService.createDashboard(user.organizationId, {
        name: 'דשבורד ראשי',
        description: 'דשבורד ברירת מחדל',
        createInitialVersion: true  // יוצג מיד
      });
    }
    console.log('✅ Dashboard loaded:', dashboard);

    dispatch(initializeDashboardSuccess(dashboard));
  } catch (error) {
    console.error('❌ Error initializing dashboard:', error);
    dispatch(initializeDashboardFailure(error.message));
  }
};

/**
 * loadDashboardById - טעינת דשבורד ספציפי לפי ID
 * משמש כשלוחצים על דשבורד מרשימת הדשבורדים
 * מותאם לשימוש חוזר בנתונים מ-Redux
 */
export const loadDashboardById = (dashboardId) => async (dispatch, getState) => {
  dispatch(initializeDashboardStart());

  try {
    console.log('📥 Loading dashboard by ID:', dashboardId);

    // בדיקה אם הדשבורד כבר קיים ב-Redux (מ-Screen component)
    const state = getState();
    let dashboard = state.screens?.dashboards?.find(d => d.dashboardId === dashboardId);

    if (dashboard) {
      console.log('✅ Dashboard found in Redux cache!');
    } else {
      // טעינה מ-Firestore רק אם לא קיים
      console.log('🔍 Dashboard not in cache, loading from Firestore...');
      dashboard = await FirestoreService.getDashboard(dashboardId);

      if (!dashboard) {
        throw new Error('Dashboard not found');
      }
      console.log('✅ Dashboard loaded from Firestore:', dashboard);
    }

    // בדיקה אם ה-organization כבר קיים ב-Redux
    let organization = state.organization;
    if (!organization || organization.organizationId !== dashboard.organizationId) {
      console.log('📥 Loading organization:', dashboard.organizationId);
      organization = await FirestoreService.getOrganization(dashboard.organizationId);
      console.log('✅ Organization loaded:', organization);

      if (!organization) {
        throw new Error('Organization not found');
      }
      dispatch(loadOrganizationSuccess(organization));
    } else {
      console.log('✅ Organization found in Redux cache!');
    }

    dispatch(initializeDashboardSuccess(dashboard));
  } catch (error) {
    console.error('❌ Error loading dashboard by ID:', error);
    dispatch(initializeDashboardFailure(error.message));
  }
};

// ============================================
// Thunk Actions - Widget Management (Local Only)
// ============================================

/**
 * addWidget - הוספת ווידג'ט לדשבורד (עדכון מקומי בלבד)
 * לא שומר ב-Firestore - רק מעדכן Redux
 */
export const addWidget = (widgetData) => async (dispatch, getState) => {
  // קח את ה-widgets הנוכחיים מה-Redux state
  const state = getState();
  const currentWidgets = state.dashboard?.dashboard?.current?.widgets || [];

  console.log('🔵 addWidget - Starting...', {
    widgetData: { widgetId: widgetData.widgetId, componentKey: widgetData.componentKey },
    currentWidgetsCount: currentWidgets.length,
    currentWidgets: currentWidgets.map(w => ({ widgetId: w.widgetId, componentKey: w.componentKey }))
  });

  const updatedWidgets = [...currentWidgets, widgetData];

  console.log('🔵 addWidget - Updated widgets:', {
    updatedWidgetsCount: updatedWidgets.length,
    updatedWidgets: updatedWidgets.map(w => ({ widgetId: w.widgetId, componentKey: w.componentKey }))
  });

  dispatch(updateDashboardLocal(updatedWidgets));
};

/**
 * copyWidgetFromDashboard - העתקת ווידג'ט מדשבורד אחר (עדכון מקומי בלבד)
 * מעתיק את כל ה-props אבל יוצר widgetId חדש
 * הגודל (w, h) נלקח מ-APP_CATALOG לפי componentKey
 * לא שומר ב-Firestore - רק מעדכן Redux
 */
export const copyWidgetFromDashboard = (widget, catalog) => async (dispatch, getState) => {
  // ייבוא uuid
  const { v4: uuidv4 } = await import('uuid');

  const state = getState();
  const currentWidgets = state.dashboard?.dashboard?.current?.widgets || [];

  // מצא את הגודל מהקטלוג לפי componentKey
  const catalogEntry = catalog.find(c => c.componentKey === widget.componentKey);
  const { w, h } = catalogEntry?.dashboardLayout || { w: 8, h: 10 };

  // צור ווידג'ט חדש עם ID חדש וגודל מהקטלוג
  const newWidget = {
    ...widget,
    widgetId: uuidv4(), // ID חדש!
    dashboardLayout: {
      ...widget.dashboardLayout,
      w, // גודל מהקטלוג
      h  // גודל מהקטלוג
      // x, y יתעדכנו ב-Dashboard.jsx לפי findFirstFreeSpace
    }
  };

  const updatedWidgets = [...currentWidgets, newWidget];
  dispatch(updateDashboardLocal(updatedWidgets));
};

/**
 * moveWidgetFromSaved - גזירת ווידג'ט מרכיבים שמורים (עדכון מקומי בלבד)
 * מעביר ווידג'ט מ-savedWidgets לדשבורד הנוכחי
 * מקבל את הווידג'ט כשהוא כבר מכיל את ה-x, y, w, h המחושבים מהדשבורד
 */
export const moveWidgetFromSaved = (widget, catalog) => async (dispatch, getState) => {
  // 🔑 שמור את ה-widgetId המקורי לפני שיוצרים חדש (כדי למחוק מהארכיון)
  const originalWidgetId = widget.widgetId;

  // ייבוא uuid
  const { v4: uuidv4 } = await import('uuid');

  const state = getState();
  const currentWidgets = state.dashboard?.dashboard?.current?.widgets || [];

  // צור ווידג'ט חדש עם ID חדש
  // שימי לב: אנחנו לא נוגעים ב-dashboardLayout כי הוא כבר הגיע מחושב (x, y פנויים) מה-Dashboard.jsx
  const newWidget = {
    ...widget,
    widgetId: uuidv4(), // רק מחליפים ID לחדש
  };

  const updatedWidgets = [...currentWidgets, newWidget];

  // עדכון הדשבורד עם הרשימה החדשה
  dispatch(updateDashboardLocal(updatedWidgets));

  // הסר מ-savedWidgets מקומית
  // 🔑 חשוב! להעביר את ה-ID המקורי, לא החדש!
  dispatch(removeFromSavedWidgetsLocal(originalWidgetId));
};

/**
 * removeFromSavedWidgetsLocal - הסרת ווידג'ט מרכיבים שמורים (מקומית בלבד)
 * מסיר ווידג'ט מ-savedWidgets ב-Redux
 * לא שומר ב-Firestore - רק מעדכן Redux
 */
export const removeFromSavedWidgetsLocal = (widgetId) => ({
  type: 'REMOVE_FROM_SAVED_WIDGETS_LOCAL',
  payload: { widgetId }
});

/**
 * removeWidget - מחיקת ווידג'ט מהדשבורד (עדכון מקומי בלבד)
 * לא שומר ב-Firestore - רק מעדכן Redux
 */
export const removeWidget = (widgetId) => async (dispatch, getState) => {
  const state = getState();
  const currentWidgets = state.dashboard?.dashboard?.current?.widgets || [];
  const updatedWidgets = currentWidgets.filter(widget => widget.widgetId !== widgetId);
  dispatch(updateDashboardLocal(updatedWidgets));
};

/**
 * updateWidget - עדכון ווידג'ט בדשבורד (עדכון מקומי בלבד)
 * לא שומר ב-Firestore - רק מעדכן Redux
 */
export const updateWidget = (widgetId, updates) => async (dispatch, getState) => {
  const state = getState();
  const currentWidgets = state.dashboard?.dashboard?.current?.widgets || [];
  const updatedWidgets = currentWidgets.map(w =>
    w.widgetId === widgetId ? updates : w
  );
  dispatch(updateDashboardLocal(updatedWidgets));
};

// ============================================
// Thunk Actions - Image Upload
// ============================================

export const uploadImage = (file) => async (dispatch) => {
  dispatch(uploadImageStart());
  try {
    const imageUrl = await StorageService.uploadImage(file, 'images');
    dispatch(uploadImageSuccess(imageUrl));
    return imageUrl;
  } catch (error) {
    dispatch(uploadImageFailure('שגיאה בהעלאת התמונה'));
    throw error;
  }
};

export const deleteImage = (imageUrl) => async () => {
  try {
    await StorageService.deleteImage(imageUrl);
  } catch (error) {
    console.error('Delete error:', error);
  }
};

// ============================================
// Thunk Actions - Version Management
// ============================================

/**
 * publishVersion - פרסום גירסה חדשה
 * משמש על ידי PublishButton
 */
export const publishVersion = (dashboardId, widgets, settings) => async (dispatch) => {
  dispatch(publishVersionStart());
  try {
    const newVersion = await FirestoreService.publishDashboard(dashboardId, widgets, settings);
    dispatch(publishVersionSuccess(newVersion));
    return newVersion;
  } catch (error) {
    console.error('Error publishing version:', error);
    dispatch(publishVersionFailure(error.message));
    throw error;
  }
};

/**
 * loadVersion - טעינת גירסה ספציפית
 * משמש על ידי ViewVersionButton
 */
export const loadVersion = (dashboardId, versionId, isPublicView = false) => async (dispatch) => {
  try {
    const version = await FirestoreService.loadVersion(dashboardId, versionId, isPublicView);
    dispatch(loadVersionSuccess(version));
    return version;
  } catch (error) {
    console.error('Error loading version:', error);
    throw error;
  }
};
